import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from '@emotion/styled';
import { collection, query, where, getDocs, doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { formatDate } from '../utils/dateFormatter';
import { SIZES } from '../firebase/schema';
import LineItem2 from './LineItem2';
import { getMockupUrl, getPersistableMockupUrl, normalizeFilesForFirestore } from '../utils/fileHelpers';
import { resolveTaxExempt, isTaxExemptFlag } from '../utils/orderSalesTax';
import { syncOrderPaymentIfInvoiceable } from '../utils/orderPayment';
import { formatOrderPoNumber } from '../utils/orderPoNumber';
import { getSizeTypeForLine, groupDocumentLinesByType } from '../utils/documentLineGroups';
import { computeOrderDocumentTotals, getOrderFees } from '../utils/orderTotals';

const InvoiceWrapper = styled.div`
  background: white;
  color: black;
  padding: 40px;
  max-width: 1000px;
  margin: 0 auto;
  font-family: 'Montserrat', Arial, sans-serif;
  text-transform: uppercase;
  
  @media print {
    padding: 0;
    max-width: 100%;
  }
  
  @page {
    size: letter;
    margin: 0.5in;
  }
`;

const InvoiceHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  align-items: center;
  margin-bottom: 40px;
  page-break-after: avoid;
`;

const BlackBox = styled.div`
  background: black;
  color: white;
  padding: 15px 25px;
  font-size: 1.2em;
  letter-spacing: 0.05em;
  
  &.left {
    text-align: left;
  }
  
  &.right {
    text-align: right;
  }

  &.footer-total {
    margin-top: 20px;
    display: inline-block;
    float: right;
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    height: 80px;
    width: auto;
  }
`;

const InfoSection = styled.div`
  margin-bottom: 30px;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 20px;
  page-break-inside: avoid;
`;

const InfoColumn = styled.div`
  .label {
    font-size: 0.8em;
    text-transform: uppercase;
    margin-bottom: 5px;
    font-weight: 600;
  }
  
  .value {
    font-size: 1em;
    border-bottom: 1px solid black;
    padding-bottom: 5px;
    min-height: 1.5em;
    display: flex;
    align-items: flex-end;
  }
`;

const EmailRow = styled.div`
  text-align: left;
  font-size: 0.9em;
  margin: 20px 0;
  color: #333;
`;

const TypeSection = styled.div`
  margin-top: 28px;

  &:first-of-type {
    margin-top: 12px;
  }
`;

const TypeSectionTitle = styled.h3`
  margin: 0 0 4px;
  padding: 8px 12px;
  background: black;
  color: white;
  font-size: 0.8em;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  page-break-after: avoid;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 20px 0;
  page-break-inside: auto;
  
  thead {
    display: table-header-group;
  }
  
  tbody {
    display: table-row-group;
  }
  
  th {
    border-bottom: 1px solid black;
    text-align: left;
    padding: 10px 5px;
    font-size: 0.75em;
    text-transform: uppercase;
    font-weight: 600;
    white-space: nowrap;
    
    &.size-header {
      text-align: center;
      padding: 2px 5px;
      font-size: 0.7em;
    }
  }
  
  tr {
    page-break-inside: avoid;
    page-break-after: auto;
  }
  
  td {
    padding: 10px 5px;
    border-bottom: 1px solid #eee;
    font-size: 0.9em;
    
    &.size-cell {
      text-align: center;
      width: 40px;
    }
    
    &.number-cell {
      text-align: right;
    }
  }
`;

const SizeHeadersRow = styled.tr`
  th {
    text-align: center;
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const SizeSubHeadersRow = styled.tr`
  th {
    text-align: center;
    font-size: 0.65em;
    font-weight: normal;
    color: #666;
    padding-top: 0;
  }
`;

const Totals = styled.div`
  margin-top: 30px;
  page-break-inside: avoid;
  
  .row {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin: 5px 0;
    gap: 20px;
    
    .label {
      text-transform: uppercase;
      font-size: 0.9em;
      font-weight: 500;
      text-align: right;
      min-width: 120px;
    }
    
    .value {
      text-align: right;
      min-width: 100px;
    }
  }
`;

const WaivedBox = styled.span`
  background: black;
  color: white;
  padding: 2px 16px;
  font-size: 0.8em;
  text-transform: uppercase;
  letter-spacing: 0.1em;
`;

const Footer = styled.div`
  margin-top: 100px;
  border-top: 1px solid black;
  padding-top: 20px;
  page-break-inside: avoid;
  
  .pickup-title {
    text-transform: uppercase;
    font-weight: 600;
    font-size: 0.9em;
    margin-bottom: 10px;
  }
  
  .pickup-text {
    font-size: 0.8em;
    color: #666;
  }
  
  .address {
    margin-top: 10px;
    font-size: 0.8em;
    display: inline-block;
  }
`;

const TaxControlsWrapper = styled.div`
  margin-bottom: 20px;
  padding: 12px;
  background: rgba(255, 215, 0, 0.1);
  border: 1px solid rgba(255, 215, 0, 0.3);
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 12px;
  
  @media print {
    display: none !important;
  }
  
  .exporting-pdf & {
    display: none !important;
  }
`;

const TaxCheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #222;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  user-select: none;
`;

const TaxCheckbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #FFD700;
`;

const TaxExemptBadge = styled.span`
  background: #FFD700;
  color: #222;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

// Edit button - hidden from print and PDF export
const EditButtonCell = styled.td`
  width: 40px;
  padding: 4px !important;
  
  @media print {
    display: none !important;
  }
  
  .exporting-pdf & {
    display: none !important;
  }
`;

const EditButtonHeader = styled.th`
  width: 40px;
  
  @media print {
    display: none !important;
  }
  
  .exporting-pdf & {
    display: none !important;
  }
`;

const EditButton = styled.button`
  background: none;
  border: none;
  padding: 4px 8px;
  cursor: pointer;
  color: #666;
  font-size: 14px;
  border-radius: 4px;
  
  &:hover {
    color: var(--al-primary);
    background-color: rgba(var(--al-primary-rgb), 0.1);
  }
  
  @media print {
    display: none !important;
  }
  
  .exporting-pdf & {
    display: none !important;
  }
`;

// Modal overlay
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const ModalBox = styled.div`
  width: 95%;
  max-width: 1200px;
  max-height: 90vh;
  overflow-y: auto;
  background-color: var(--al-bg);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background-color: var(--al-surface);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const ModalTitle = styled.h3`
  color: var(--al-primary);
  font-weight: 600;
  text-transform: uppercase;
  font-family: 'Montserrat', sans-serif;
  font-size: 1.1rem;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  padding: 4px 8px;
  line-height: 1;
  
  &:hover {
    color: var(--al-primary);
  }
`;

const ModalContent = styled.div`
  padding: 20px;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 20px;
  background-color: var(--al-surface);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const CancelButton = styled.button`
  background: none;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: rgba(255, 255, 255, 0.7);
  padding: 10px 20px;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  
  &:hover {
    color: white;
    border-color: white;
  }
`;

const SaveButton = styled.button`
  background: var(--al-primary);
  border: none;
  color: #222;
  padding: 10px 20px;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  
  &:hover {
    background: #7FE87F;
  }
  
  &:disabled {
    background: #666;
    color: #999;
    cursor: not-allowed;
  }
`;

const Invoice = ({ order, onUpdate, onOrderCompleted, invoiceRef: externalRef }) => {
  const [lineItems, setLineItems] = useState([]);
  const [loading, setLoading] = useState(true);
  // Tax exempt state - initialized as null until we fetch customer data
  const [taxExemptOverride, setTaxExemptOverride] = useState(null);
  const [customerTaxExempt, setCustomerTaxExempt] = useState(false);
  const [orderTaxExempt, setOrderTaxExempt] = useState(false);
  const internalRef = useRef(null);
  const invoiceRef = externalRef || internalRef;
  
  // Edit line state
  const [editingLine, setEditingLine] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editedLineData, setEditedLineData] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const orderFees = getOrderFees(order);

  // Fetch line items function - extracted for reuse
  const fetchLineItems = useCallback(async () => {
    try {
      const q = query(
        collection(db, 'orderLines'),
        where('orderId', '==', order.id)
      );
      const querySnapshot = await getDocs(q);
      const items = querySnapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
      setLineItems(items);
    } catch (error) {
      console.error('Error fetching line items:', error);
    }
  }, [order.id]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch line items
        await fetchLineItems();

        const orderSaysTaxExempt =
          isTaxExemptFlag(order.taxExempt) ||
          isTaxExemptFlag(order.additionalInstructions?.taxExempt);
        setOrderTaxExempt(orderSaysTaxExempt);

        let customerData = null;
        if (order.customerId) {
          const customerDoc = await getDoc(doc(db, 'customers', order.customerId));
          if (customerDoc.exists()) {
            customerData = customerDoc.data();
          }
        }

        // Fallback: some older orders only store customerName — still honor profile tax exempt.
        if (!customerData && order.customerName) {
          const customersSnap = await getDocs(
            query(
              collection(db, 'customers'),
              where('client', '==', order.customerName)
            )
          );
          if (!customersSnap.empty) {
            // Prefer a tax-exempt match if duplicates exist under the same name.
            const docs = customersSnap.docs.map((d) => d.data());
            customerData =
              docs.find((c) => isTaxExemptFlag(c.taxExempt)) || docs[0];
          }
        }

        const customerExempt = isTaxExemptFlag(customerData?.taxExempt);
        setCustomerTaxExempt(customerExempt);

        const derivedExempt = resolveTaxExempt({
          order,
          customer: customerData,
        });

        // Stale invoiceTaxExempt:false on tax-exempt customers was locking the checkbox off.
        if (customerExempt && order.invoiceTaxExempt === false) {
          setTaxExemptOverride(true);
          try {
            await updateDoc(doc(db, 'orders', order.id), {
              invoiceTaxExempt: true,
              updatedAt: serverTimestamp(),
            });
            onUpdate?.({ invoiceTaxExempt: true });
          } catch (persistError) {
            console.warn('Could not clear stale invoice tax override:', persistError);
          }
        } else if (order.invoiceTaxExempt === true || order.invoiceTaxExempt === false) {
          setTaxExemptOverride(order.invoiceTaxExempt);
        } else {
          setTaxExemptOverride(derivedExempt);
        }
      } catch (error) {
        console.error('Error fetching invoice data:', error);
        const fallbackOrderExempt =
          isTaxExemptFlag(order.taxExempt) ||
          isTaxExemptFlag(order.additionalInstructions?.taxExempt);
        setTaxExemptOverride(fallbackOrderExempt);
      } finally {
        setLoading(false);
      }
    };
    if (order?.id) {
      fetchData();
    }
  }, [
    order?.id,
    order.customerId,
    order.customerName,
    order.invoiceTaxExempt,
    order.taxExempt,
    order.additionalInstructions?.taxExempt,
    fetchLineItems
  ]);

  // Handle opening the edit modal
  const handleEditLine = (line) => {
    // Convert line data to LineItem2 format
    const lineItem2Data = {
      id: line.id,
      design: line.designName,
      designName: line.designName,
      type: line.type || 'Screen Print',
      style: line.garmentId,
      color: line.garmentColor,
      sizeType: getSizeTypeForLine(line),
      sizes: line.sizes || {},
      price: line.pricePerPiece,
      pricePerPiece: line.pricePerPiece,
      cogs: line.cogs,
      frontInkColors: line.frontInkColors || [],
      backInkColors: line.backInkColors || [],
      otherInkColors: line.otherInkColors || [],
      printLocations: line.printLocations || [],
      inkNotes: line.inkNotes,
      files: line.files,
      // Pass through other fields
      ...line,
      // Open expanded by default for invoice editing
      isExpanded: true
    };
    setEditingLine(line);
    setEditedLineData(lineItem2Data);
    setEditModalOpen(true);
  };

  // Handle line data updates from LineItem2
  const handleLineDataUpdate = (updatedData) => {
    setEditedLineData(updatedData);
  };

  // Handle saving the edited line
  const handleSaveEditedLine = async () => {
    if (!editingLine || !editedLineData) return;
    
    setSaving(true);
    try {
      const lineRef = doc(db, 'orderLines', editingLine.id);
      
      // Calculate total quantity from sizes
      const totalQuantity = editedLineData.sizes 
        ? Object.values(editedLineData.sizes).reduce((sum, qty) => sum + (Number(qty) || 0), 0)
        : 0;
      
      // Get mockup URL using helper function
      const mockupUrl = getMockupUrl(editedLineData);
      
      // Normalize files for Firestore (removes File objects, etc.)
      const normalizedFiles = normalizeFilesForFirestore(editedLineData.files);
      
      // Build update object - IMPORTANT: Include files and designFileUrl to preserve them
      const updateData = {
        designName: editedLineData.design || editedLineData.designName,
        garmentId: editedLineData.style,
        garmentColor: editedLineData.color,
        sizeType: editedLineData.sizeType,
        sizes: editedLineData.sizes || {},
        totalQuantity,
        pricePerPiece: parseFloat(editedLineData.price || editedLineData.pricePerPiece) || 0,
        cogs: parseFloat(editedLineData.cogs) || 0,
        type: editedLineData.type,
        frontInkColors: editedLineData.frontInkColors || [],
        backInkColors: editedLineData.backInkColors || [],
        otherInkColors: editedLineData.otherInkColors || [],
        printLocations: editedLineData.printLocations || [],
        inkNotes: editedLineData.inkNotes || null,
        updatedAt: serverTimestamp(),
        invoiceEditedAt: serverTimestamp(), // Track when invoice edits happen
        // Preserve file-related fields
        ...(normalizedFiles && { files: normalizedFiles }),
        ...(getPersistableMockupUrl(mockupUrl) && { designFileUrl: getPersistableMockupUrl(mockupUrl) })
      };
      
      await updateDoc(lineRef, updateData);
      
      // Refresh line items to show updated data
      await fetchLineItems();
      
      // Close modal
      setEditModalOpen(false);
      setEditingLine(null);
      setEditedLineData(null);
      
      console.log('✅ Line updated successfully');
    } catch (error) {
      console.error('Error saving line:', error);
      alert('Failed to save changes: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  // Handle closing the edit modal
  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditingLine(null);
    setEditedLineData(null);
  };

  const handleTaxExemptChange = async (checked) => {
    setTaxExemptOverride(checked);
    try {
      await updateDoc(doc(db, 'orders', order.id), {
        invoiceTaxExempt: checked,
        updatedAt: serverTimestamp(),
      });
      await syncOrderPaymentIfInvoiceable(db, order.id);
      onUpdate?.({ invoiceTaxExempt: checked });
    } catch (error) {
      console.error('Error saving tax exempt setting:', error);
    }
  };

  const totals = computeOrderDocumentTotals(order, lineItems, {
    customer: customerTaxExempt ? { taxExempt: true } : null,
    invoiceTaxExempt:
      taxExemptOverride === true ? true : taxExemptOverride === false ? false : order.invoiceTaxExempt,
    fees: orderFees
  });

  const invoiceSections = groupDocumentLinesByType(lineItems);

  if (loading) {
    return <div>Loading invoice details...</div>;
  }

  return (
    <div>
      {/* Tax controls - NOT included in PDF */}
      <TaxControlsWrapper>
        <TaxCheckboxLabel>
          <TaxCheckbox
            type="checkbox"
            checked={taxExemptOverride === true}
            onChange={(e) => handleTaxExemptChange(e.target.checked)}
          />
          <span>Tax Exempt This Invoice</span>
        </TaxCheckboxLabel>
        {customerTaxExempt && (
          <TaxExemptBadge>Customer is Tax Exempt</TaxExemptBadge>
        )}
        {orderTaxExempt && !customerTaxExempt && (
          <TaxExemptBadge>Order marked Tax Exempt</TaxExemptBadge>
        )}
      </TaxControlsWrapper>

      <InvoiceWrapper ref={invoiceRef} data-invoice-content>
        <InvoiceHeader>
          <BlackBox className="left">
            INVOICE NO.<br />
            {formatOrderPoNumber(order)}
            {order.quoteNumber && (
              <div style={{ fontSize: '0.55em', marginTop: '6px', fontWeight: 500, lineHeight: 1.3 }}>
                Quote {order.quoteNumber}
              </div>
            )}
          </BlackBox>
          <Logo><img src="/images/87c-id-144x144.png" alt="87 Central" /></Logo>
          <BlackBox className="right">
            TOTAL<br />
            ${totals.total.toFixed(2)}
          </BlackBox>
        </InvoiceHeader>

        <InfoGrid>
          <InfoColumn>
            <div className="label">Client</div>
            <div className="value">{order.client || order.customerName}</div>
          </InfoColumn>
          <InfoColumn>
            <div className="label">PO</div>
            <div className="value">{order.customerPoNumber || '—'}</div>
          </InfoColumn>
          <InfoColumn>
            <div className="label">Manager</div>
            <div className="value">{order.projectManager}</div>
          </InfoColumn>
          <InfoColumn>
            <div className="label">Date</div>
            <div className="value">{formatDate(order.dueDate)}</div>
          </InfoColumn>
        </InfoGrid>

        {order.contactEmail && (
          <EmailRow>{order.contactEmail}</EmailRow>
        )}

        {invoiceSections.map(({ lineType, sizeGroups }) => (
          <TypeSection key={lineType}>
            <TypeSectionTitle>{lineType}</TypeSectionTitle>
            {sizeGroups.map(({ sizeType, lines: itemsInGroup }) => {
              const sizesForType = SIZES[sizeType] || [];
              return (
                <Table key={`${lineType}-${sizeType}`}>
                  <thead>
                    <tr>
                      <EditButtonHeader>{/* Edit */}</EditButtonHeader>
                      <th>Description</th>
                      <th>Style</th>
                      <th>Color</th>
                      {sizesForType.map(size => (
                        <th key={size} className="size-header">{size}</th>
                      ))}
                      <th style={{ textAlign: 'right' }}>QTY</th>
                      <th style={{ textAlign: 'right' }}>RATE</th>
                      <th style={{ textAlign: 'right' }}>AMOUNT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {itemsInGroup.map(line => {
                      const styleNumber = line.garmentId?.split(' - ')[0] || line.garmentId || '';
                      return (
                      <tr key={line.id}>
                        <EditButtonCell>
                          <EditButton
                            onClick={() => handleEditLine(line)}
                            title="Edit line"
                          >
                            ✏️
                          </EditButton>
                        </EditButtonCell>
                        <td>{line.designName}</td>
                        <td>{styleNumber}</td>
                        <td>{line.garmentColor}</td>
                        {sizesForType.map(size => (
                          <td key={size} className="size-cell">
                            {line.sizes?.[size] || 0}
                          </td>
                        ))}
                        <td className="number-cell">{line.totalQuantity}</td>
                        <td className="number-cell">${parseFloat(line.pricePerPiece || 0).toFixed(2)}</td>
                        <td className="number-cell">
                          ${(line.totalQuantity * (parseFloat(line.pricePerPiece) || 0)).toFixed(2)}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </Table>
              );
            })}
          </TypeSection>
        ))}

        <Totals>
          <div className="row">
            <div className="label">Sub Total</div>
            <div className="value">
              ${totals.subtotal.toFixed(2)}
            </div>
          </div>
          
          {/* Display 2XL+ upcharge if applicable */}
          {totals.upchargeTotal > 0 && (
            <div className="row" style={{ fontSize: '0.85em', color: '#666' }}>
              <div className="label">(Includes 2XL+ Upcharge)</div>
              <div className="value">${totals.upchargeTotal.toFixed(2)}</div>
            </div>
          )}
          
          {/* Display discount if applicable */}
          {totals.discountAmount > 0 && (
            <div className="row">
              <div className="label">
                Discount {order.discount?.type === 'percent' ? `(${order.discount.amount}%)` : ''}
              </div>
              <div className="value" style={{ color: '#dc3545' }}>
                -${totals.discountAmount.toFixed(2)}
              </div>
            </div>
          )}
          
          {/* Display fees from order */}
          {orderFees.length > 0 ? (
            orderFees.map((fee, index) => {
              const quantity = parseFloat(fee.quantity) || 0;
              const price = parseFloat(fee.price) || 0;
              const feeTotal = quantity * price;
              return (
                <div className="row" key={fee.id || index}>
                  <div className="label">{fee.type}</div>
                  <div className="value">${feeTotal.toFixed(2)}</div>
                </div>
              );
            })
          ) : (
            // Show waived fees if no fees are set
            <>
              <div className="row">
                <div className="label">Artwork Fee</div>
                <div><WaivedBox>Waived</WaivedBox></div>
                <div className="value">$0.00</div>
              </div>
              <div className="row">
                <div className="label">Setup Fee</div>
                <div><WaivedBox>Waived</WaivedBox></div>
                <div className="value">$0.00</div>
              </div>
            </>
          )}
          
          <div className="row">
            <div className="label">Sales Tax</div>
            {taxExemptOverride && <div><WaivedBox>Tax Exempt</WaivedBox></div>}
            <div className="value">${totals.tax.toFixed(2)}</div>
          </div>
        </Totals>

        <Footer>
          <div className="pickup-title">Due at Pickup</div>
          <div className="pickup-text">
            Orders have been counted. Any discrepancies must be addressed at time of pickup.<br />
            Please note we accept all major credit cards. Please make checks payable to:
          </div>
          <div className="address">
            Eight Seven Central<br />
            863 42nd Street<br />
            Des Moines, IA 50312
          </div>
          <BlackBox className="footer-total">
            TOTAL<br />
            ${totals.total.toFixed(2)}
          </BlackBox>
        </Footer>
      </InvoiceWrapper>

      {/* Edit Line Modal */}
      {editModalOpen && (
        <ModalOverlay onClick={handleCloseEditModal}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>✏️ Edit Line Item</ModalTitle>
              <CloseButton onClick={handleCloseEditModal}>×</CloseButton>
            </ModalHeader>
            
            <ModalContent>
              {editedLineData && (
                <LineItem2
                  lineData={editedLineData}
                  index={0}
                  onUpdate={handleLineDataUpdate}
                  onDelete={() => {}} // Don't allow delete from invoice edit
                  onDuplicate={() => {}} // Don't allow duplicate from invoice edit
                  orderNumber={order.poNumber || order.orderNumber}
                  lineId={editingLine?.id}
                />
              )}
            </ModalContent>
            
            <ModalActions>
              <CancelButton onClick={handleCloseEditModal}>
                Cancel
              </CancelButton>
              <SaveButton
                onClick={handleSaveEditedLine}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </SaveButton>
            </ModalActions>
          </ModalBox>
        </ModalOverlay>
      )}
    </div>
  );
};

export default Invoice; 